import { Check, Phone, Mail, MapPin, Globe } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { absorbentContent, pinkwaterContent, spillContent, spillServices, company } from "../mock";

const SITE_URL = process.env.REACT_APP_BACKEND_URL;
const PRODUCT_PATH = { absorbent: "/absorbent", pinkwater: "/pinkwater", spill: "/spill-solutions" };

const FLYERS = {
  absorbent: {
    accent: "#1b75bc",
    accentDark: "#155e97",
    kicker: "Drying Agent / Absorbent",
    brandName: "BoneDry\u2122",
    title: absorbentContent.header,
    heroImage: absorbentContent.headerImage,
    lead: absorbentContent.lead,
    stats: absorbentContent.stats,
    colA: {
      heading: "Why BoneDry\u2122",
      items: absorbentContent.features.map((f) => `${f.title} \u2014 ${f.text}`),
    },
    colB: {
      heading: "Also Available",
      items: [
        "Unsinkable oil absorbent Socks, Booms & Bilge Mini-Booms for spills on any body of water.",
        "Absorbs oils, fuels, lubricants and hydrocarbons \u2014 never water.",
        "Heavy-duty stainless steel hardware and nylon mesh jackets.",
        "Available in 2 cu. ft. bags, 3 lb. resealable bags and Super Sacks.",
      ],
    },
  },
  pinkwater: {
    accent: "#d81b76",
    accentDark: "#b5155f",
    kicker: "Pinkwater\u00ae Surfactants",
    brandName: "Pinkwater\u00ae",
    title: pinkwaterContent.headline,
    heroImage: pinkwaterContent.headerImage,
    lead: pinkwaterContent.lead,
    stats: pinkwaterContent.stats,
    colA: {
      heading: "Applications Include",
      items: pinkwaterContent.applications,
    },
    colB: {
      heading: "Key Benefits",
      items: pinkwaterContent.benefits,
    },
  },
  spill: {
    accent: "#0f6b82",
    accentDark: "#0c586b",
    kicker: "Spill Solutions",
    brandName: "Spill Solutions",
    title: spillContent.title,
    heroImage: spillContent.headerImage,
    lead: spillContent.positioning,
    stats: [
      { value: "9", label: "Dedicated service programs" },
      { value: "Full", label: "Spill lifecycle coverage" },
      { value: "PFAS-Free", label: "Absorbent options available" },
    ],
    colA: {
      heading: "Service Programs",
      items: spillServices.map((s) => s.title),
    },
    colB: {
      heading: "The Partnership Difference",
      items: [
        "Full spill lifecycle: assessment, prevention, response, training & compliance.",
        "Scheduled service, inspections and replenishment programs that reduce risk.",
        "Emergency response contracts and on-site spill kit management.",
        "PFAS-free absorbent recommendations for sensitive applications.",
        "Expertise that shifts focus from price-per-bag to measurable value.",
        "Long-term partnerships built on compliance and cost control.",
      ],
    },
  },
};

function FlyerLogo() {
  return (
    <div className="inline-flex flex-col items-start leading-none">
      <div className="flex items-center" style={{ fontFamily: "Oswald, sans-serif" }}>
        <span className="text-[22px] font-bold tracking-tight text-white">MARKET</span>
        <img src="/msv-circle.png" alt="" className="w-[20px] h-[20px] mx-[5px] object-contain" />
        <span className="text-[22px] font-bold tracking-tight text-white">STREET</span>
      </div>
      <span
        className="text-[10px] tracking-[0.5em] pl-[0.5em] mt-0.5 text-white/70"
        style={{ fontFamily: "Oswald, sans-serif" }}
      >
        VENTURES
      </span>
    </div>
  );
}

export default function ProductFlyer({ product }) {
  const f = FLYERS[product] || FLYERS.absorbent;

  return (
    <>
      <style>{`
        @page { size: A4; margin: 0; }
        html, body { margin: 0; padding: 0; background: #ffffff; }
        .msv-flyer { width: 210mm; min-height: 297mm; }
      `}</style>
      <div
        data-testid="product-flyer"
        className="msv-flyer mx-auto bg-white flex flex-col font-body text-[#2a2a2a]"
      >
        {/* Header */}
        <div className="bg-[#1a2540] px-[16mm] py-6 flex items-center justify-between">
          <FlyerLogo />
          <span
            className="text-[11px] uppercase tracking-[0.3em] text-white/60"
            style={{ fontFamily: "Oswald, sans-serif" }}
          >
            Product Flyer
          </span>
        </div>
        <div style={{ height: "5px", backgroundColor: f.accent }} />

        {/* Hero */}
        <div className="relative h-[62mm] overflow-hidden">
          <img src={f.heroImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div
            className="absolute inset-0"
            style={{ background: `linear-gradient(90deg, rgba(26,37,64,0.92) 0%, rgba(26,37,64,0.55) 100%)` }}
          />
          <div className="relative h-full flex flex-col justify-center px-[16mm]">
            <p
              className="uppercase tracking-[0.28em] text-[11px] mb-2"
              style={{ color: f.accent === "#d81b76" ? "#ff9ecb" : "#8fc4ee", fontFamily: "Oswald, sans-serif" }}
            >
              {f.kicker}
            </p>
            <h1
              className="text-white text-[30px] leading-[1.1] font-bold max-w-[150mm]"
              style={{ fontFamily: "Oswald, sans-serif" }}
            >
              {f.title}
            </h1>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 px-[16mm] py-8 flex flex-col">
          <p className="text-[13px] leading-relaxed text-[#3a3a3a] mb-7">{f.lead}</p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            {f.stats.map((s) => (
              <div
                key={s.label}
                className="rounded-lg px-4 py-4 text-center"
                style={{ backgroundColor: "#f4f7fa", borderTop: `3px solid ${f.accent}` }}
              >
                <p
                  className="text-[24px] font-bold leading-none mb-2"
                  style={{ color: f.accentDark, fontFamily: "Oswald, sans-serif" }}
                >
                  {s.value}
                </p>
                <p className="text-[10px] leading-snug text-[#5a5a5a]">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Two columns */}
          <div className="grid grid-cols-2 gap-8">
            {[f.colA, f.colB].map((col) => (
              <div key={col.heading}>
                <h3
                  className="uppercase tracking-[0.08em] text-[13px] mb-4 pb-2"
                  style={{
                    color: "#1a2540",
                    fontFamily: "Oswald, sans-serif",
                    borderBottom: `2px solid ${f.accent}`,
                  }}
                >
                  {col.heading}
                </h3>
                <ul className="space-y-2.5">
                  {col.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check size={14} className="mt-0.5 shrink-0" style={{ color: f.accent }} />
                      <span className="text-[11px] leading-snug text-[#3a3a3a]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto bg-[#1a2540] text-white px-[16mm] py-6">
          <div className="grid grid-cols-[1fr_auto] gap-6 items-center">
            <div className="grid grid-cols-2 gap-6 items-center">
              <div>
                <p className="text-[13px] font-bold mb-1" style={{ fontFamily: "Oswald, sans-serif" }}>
                  {company.name}
                </p>
                <p className="text-[10px] text-white/70 leading-snug flex items-start gap-1.5">
                  <MapPin size={12} className="mt-0.5 shrink-0" />
                  <span>
                    {company.address.line1}, {company.address.line2}
                  </span>
                </p>
              </div>
              <div className="space-y-1.5">
                <p className="text-[11px] flex items-center gap-1.5">
                  <Phone size={12} /> {company.phone}
                </p>
                <p className="text-[11px] flex items-center gap-1.5">
                  <Mail size={12} /> {company.email}
                </p>
                <p className="text-[11px] flex items-center gap-1.5 text-white/70">
                  <Globe size={12} /> marketstreetventures.com
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <div className="bg-white p-1.5 rounded-md">
                <QRCodeSVG
                  value={`${SITE_URL}${PRODUCT_PATH[product] || "/"}`}
                  size={64}
                  level="M"
                  fgColor="#1a2540"
                  bgColor="#ffffff"
                />
              </div>
              <span
                className="text-[8px] uppercase tracking-[0.15em] text-white/60"
                style={{ fontFamily: "Oswald, sans-serif" }}
              >
                Scan for details
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
